// Location detection utility for automatic currency selection

export interface LocationInfo {
  country: string;
  countryCode: string;
  currencyCode: string;
}

// Country to currency mapping
const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  'EG': 'EGP', // Egypt
  'US': 'USD', // United States
  'GB': 'GBP', // United Kingdom
  'EU': 'EUR', // European Union (fallback)
  'DE': 'EUR', // Germany
  'FR': 'EUR', // France
  'IT': 'EUR', // Italy
  'ES': 'EUR', // Spain
  'CA': 'CAD', // Canada
  'AU': 'AUD', // Australia
  'JP': 'JPY', // Japan
  'CN': 'CNY', // China
  'IN': 'INR', // India
  'SA': 'SAR', // Saudi Arabia
  'AE': 'AED', // UAE
};

// Detect user's location and return appropriate currency without permission requests
export const detectUserCurrency = async (): Promise<string> => {
  try {
    // Skip geolocation entirely and use fallback methods only
    return await fallbackCurrencyDetection();
  } catch (error) {
    console.log('Location detection failed:', error);
    return 'USD'; // Default fallback
  }
};

// Fallback detection methods when geolocation is not available
const fallbackCurrencyDetection = async (): Promise<string> => {
  try {
    // Try timezone detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currencyFromTimezone = getCurrencyFromTimezone(timezone);
    if (currencyFromTimezone) {
      return currencyFromTimezone;
    }

    // Try language/locale detection
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    const currencyFromLocale = getCurrencyFromLocale(locale);
    if (currencyFromLocale) {
      return currencyFromLocale;
    }

    // Try IP-based detection (free service)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    const countryCode = data.country_code;
    const currency = COUNTRY_CURRENCY_MAP[countryCode] || 'USD';
    return currency;
  } catch (error) {
    console.log('Fallback detection failed:', error);
    return 'USD'; // Ultimate fallback
  }
};

// Get currency from timezone
const getCurrencyFromTimezone = (timezone: string): string | null => {
  const timezoneCountryMap: Record<string, string> = {
    'Africa/Cairo': 'EGP',
    'America/New_York': 'USD',
    'America/Los_Angeles': 'USD',
    'America/Chicago': 'USD',
    'America/Toronto': 'CAD',
    'Europe/London': 'GBP',
    'Europe/Paris': 'EUR',
    'Europe/Berlin': 'EUR',
    'Europe/Rome': 'EUR',
    'Europe/Madrid': 'EUR',
    'Asia/Tokyo': 'JPY',
    'Asia/Shanghai': 'CNY',
    'Asia/Kolkata': 'INR',
    'Asia/Riyadh': 'SAR',
    'Asia/Dubai': 'AED',
    'Australia/Sydney': 'AUD',
  };
  
  return timezoneCountryMap[timezone] || null;
};

// Get currency from locale
const getCurrencyFromLocale = (locale: string): string | null => {
  const localeCountryMap: Record<string, string> = {
    'ar-EG': 'EGP',
    'en-US': 'USD',
    'en-GB': 'GBP',
    'en-CA': 'CAD',
    'en-AU': 'AUD',
    'de-DE': 'EUR',
    'fr-FR': 'EUR',
    'it-IT': 'EUR',
    'es-ES': 'EUR',
    'ja-JP': 'JPY',
    'zh-CN': 'CNY',
    'hi-IN': 'INR',
    'ar-SA': 'SAR',
    'ar-AE': 'AED',
  };
  
  return localeCountryMap[locale] || null;
};

// Check if a currency is supported
export const isSupportedCurrency = (currencyCode: string): boolean => {
  return Object.values(COUNTRY_CURRENCY_MAP).includes(currencyCode);
};